import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { AccountResponse } from 'api/account';
import { pinCampaign, unpinCampaign } from 'api/campaigns';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { queryClient } from 'queries/queryClient';
import { Campaign } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface PinCampaignMutationContext {
    foundCampaignIndex: number;
}
const usePinCampaign = (
    options: UseMutationOptions<
        unknown,
        GivebutterAxiosError,
        Campaign,
        PinCampaignMutationContext
    > = {},
) => {
    const { accountId, pinnedCampaigns: pinned, recentCampaigns: recent } = useCurrentAccount();
    const { error } = useToast();
    const { onSuccess, ...restOptions } = options;

    return useMutation<unknown, GivebutterAxiosError, Campaign, PinCampaignMutationContext>({
        mutationFn: campaign => {
            return campaign.is_pinned ? unpinCampaign(campaign.id) : pinCampaign(campaign.id);
        },
        onMutate: campaign => {
            // optimistically update at Account/Menu level
            const updated = { ...campaign, is_pinned: !campaign.is_pinned };
            const foundCampaignIndex = pinned.findIndex(c => c.id === campaign.id);
            if (foundCampaignIndex >= 0) {
                queryClient.setQueryData<AccountResponse>(
                    accountQueryKeys.current(accountId),
                    oldData => {
                        if (!oldData) return undefined;
                        return {
                            ...oldData,
                            menu: {
                                ...oldData.menu,
                                pinned_campaigns: pinned.filter(c => c.id !== updated.id),
                                recent_campaigns: recent.map(c =>
                                    c.id === updated.id ? updated : c,
                                ),
                            },
                        };
                    },
                );
            } else {
                queryClient.setQueryData<AccountResponse>(
                    accountQueryKeys.current(accountId),
                    oldData => {
                        if (!oldData) return undefined;
                        return {
                            ...oldData,
                            menu: {
                                ...oldData.menu,
                                pinned_campaigns: [updated, ...pinned],
                                recent_campaigns: recent.map(c =>
                                    c.id === updated.id ? updated : c,
                                ),
                            },
                        };
                    },
                );
            }

            // optimistically update campaign detail cache
            queryClient.setQueryData<Campaign>(campaignQueryKeys.campaign(campaign.id), oldData => {
                if (!oldData) return undefined;
                return {
                    ...oldData,
                    is_pinned: !oldData.is_pinned,
                };
            });

            return { foundCampaignIndex };
        },
        onSuccess: (response, campaign, context) => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.campaigns(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaign.id),
            });
            onSuccess?.(response, campaign, context);
        },
        onError: (e, campaign, context) => {
            const { is_pinned: isPinned, id } = campaign;
            error(e.response?.data.message || `Unable to ${isPinned ? 'unpin' : 'pin'} campaign.`);

            // undo optimistic update at Account/Menu level
            if (context?.foundCampaignIndex && context.foundCampaignIndex !== -1) {
                queryClient.setQueryData<AccountResponse>(
                    accountQueryKeys.current(accountId),
                    oldData => {
                        if (!oldData) return undefined;
                        return {
                            ...oldData,
                            menu: {
                                ...oldData.menu,
                                pinned_campaigns: pinned.splice(
                                    context.foundCampaignIndex,
                                    0,
                                    campaign,
                                ),
                                recent_campaigns: recent.map(c =>
                                    c.id === campaign.id ? campaign : c,
                                ),
                            },
                        };
                    },
                );
            } else {
                queryClient.setQueryData<AccountResponse>(
                    accountQueryKeys.current(accountId),
                    oldData => {
                        if (!oldData) return undefined;
                        return {
                            ...oldData,
                            menu: {
                                ...oldData.menu,
                                pinned_campaigns: pinned.filter(c => c.id !== id),
                                recent_campaigns: recent.map(c =>
                                    c.id === campaign.id ? campaign : c,
                                ),
                            },
                        };
                    },
                );
            }

            // undo optimistic update in campaign detail cache
            queryClient.setQueryData<Campaign>(campaignQueryKeys.campaign(campaign.id), campaign);
        },
        ...restOptions,
    });
};

export { usePinCampaign };
